(() => {
  const theme = window.theme || {};

  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.thumbs = [...this.querySelectorAll('.thumb')];
      this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.select(thumb.dataset.mediaId)));
      const list = this.querySelector('.thumbs');
      if (list) {
        list.addEventListener('keydown', (e) => {
          if (!['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight'].includes(e.key)) return;
          e.preventDefault();
          const i = this.thumbs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
          const dir = e.key === 'ArrowDown' || e.key === 'ArrowRight' ? 1 : -1;
          const next = this.thumbs[(i + dir + this.thumbs.length) % this.thumbs.length];
          this.select(next.dataset.mediaId);
          next.focus();
        });
      }
      if (this.querySelector('model-viewer') && window.Shopify && Shopify.loadFeatures) {
        Shopify.loadFeatures([
          {
            name: 'model-viewer-ui',
            version: '1.0',
            onLoad: (errors) => {
              if (errors) return;
              this.querySelectorAll('model-viewer').forEach((m) => {
                if (window.Shopify.ModelViewerUI) new window.Shopify.ModelViewerUI(m);
              });
            }
          },
          {
            name: 'shopify-xr',
            version: '1.0',
            onLoad: (errors) => {
              if (errors || !window.ShopifyXR) return;
              document.querySelectorAll('[id^="ProductJSON-"]').forEach((el) => {
                window.ShopifyXR.addModels(JSON.parse(el.textContent));
              });
              window.ShopifyXR.setupXRElements();
            }
          }
        ]);
      }
      this.initZoom();
    }

    initZoom() {
      const mode = this.dataset.zoom;
      if (!mode) return;
      this.lightbox = document.getElementById(this.dataset.lightbox);
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-zoom-open]');
        const view = !btn && e.target.closest('.view.on[data-media-type="image"]');
        const id = btn ? btn.dataset.zoomOpen : view && view.dataset.mediaId;
        if (id && this.lightbox) this.openLightbox(id, btn || view.querySelector('[data-zoom-open]'));
      });
      if (mode === 'hover' && matchMedia('(hover: hover)').matches && !theme.reduceMotion) {
        this.querySelectorAll('.view[data-media-type="image"]').forEach((view) => {
          const img = view.querySelector('img');
          if (!img) return;
          view.addEventListener('pointermove', (e) => {
            const r = view.getBoundingClientRect();
            img.style.transformOrigin = `${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`;
            view.classList.add('is-zoomed');
          });
          view.addEventListener('pointerleave', () => view.classList.remove('is-zoomed'));
        });
      }
      if (!this.lightbox) return;
      this.track = this.lightbox.querySelector('[data-lb-track]');
      this.slides = [...this.lightbox.querySelectorAll('.lb-slide')];
      const go = (dir) => {
        const i = this.currentSlide();
        const next = this.slides[(i + dir + this.slides.length) % this.slides.length];
        if (next) this.track.scrollTo({ left: next.offsetLeft, behavior: theme.reduceMotion ? 'auto' : 'smooth' });
      };
      this.lightbox.querySelector('[data-lb-prev]')?.addEventListener('click', () => go(-1));
      this.lightbox.querySelector('[data-lb-next]')?.addEventListener('click', () => go(1));
      this.lightbox.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') go(1);
        if (e.key === 'ArrowLeft') go(-1);
      });
      let t;
      this.track.addEventListener('scroll', () => {
        clearTimeout(t);
        t = setTimeout(() => this.updateCount(), 80);
      });
    }

    currentSlide() {
      const left = this.track.scrollLeft;
      let best = 0;
      this.slides.forEach((s, i) => {
        if (Math.abs(s.offsetLeft - left) < Math.abs(this.slides[best].offsetLeft - left)) best = i;
      });
      return best;
    }

    updateCount() {
      const count = this.lightbox.querySelector('[data-lb-count]');
      if (count) count.textContent = `${this.currentSlide() + 1} / ${this.slides.length}`;
    }

    openLightbox(mediaId, opener) {
      theme.openDialog(this.lightbox, opener);
      const slide = this.slides.find((s) => s.dataset.mediaId === String(mediaId));
      if (slide) {
        const img = slide.querySelector('img');
        if (img) img.loading = 'eager';
        this.track.scrollTo({ left: slide.offsetLeft, behavior: 'auto' });
      }
      this.updateCount();
      const close = this.lightbox.querySelector('[data-close]');
      if (close) close.focus();
    }

    select(mediaId) {
      if (!mediaId) return;
      this.querySelectorAll('.view[data-media-id]').forEach((view) => {
        const on = view.dataset.mediaId === String(mediaId);
        if (!on && view.classList.contains('on')) {
          view.querySelectorAll('video').forEach((v) => v.pause());
          view.querySelectorAll('iframe').forEach((f) => {
            f.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
            f.contentWindow?.postMessage('{"method":"pause"}', '*');
          });
        }
        view.classList.toggle('on', on);
      });
      this.thumbs.forEach((t) => {
        const on = t.dataset.mediaId === String(mediaId);
        t.setAttribute('aria-selected', String(on));
        t.tabIndex = on ? 0 : -1;
      });
    }
  }
  if (!customElements.get('media-gallery')) customElements.define('media-gallery', MediaGallery);

  class ProductInfo extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      const json = this.querySelector('[data-variants]');
      this.variants = json ? JSON.parse(json.textContent) : [];
      this.picker = this.querySelector('[data-variant-picker]');
      if (this.picker) {
        this.picker.addEventListener('change', (e) => {
          const option = e.target.tagName === 'SELECT' ? e.target.selectedOptions[0] : null;
          if (option && option.dataset.productUrl) {
            window.location.href = option.dataset.productUrl;
            return;
          }
          this.onVariantChange();
        });
      }
      this.initBuyingMode();
      this.initSticky();
      if (this.variants.length) this.updateAvailability(this.selectedOptions());
    }

    selectedOptions() {
      if (!this.picker) return [];
      const groups = {};
      this.picker.querySelectorAll('[data-option-index]').forEach((el) => {
        const index = Number(el.dataset.optionIndex);
        if (el.tagName === 'SELECT') groups[index] = el.value;
        else if (el.checked) groups[index] = el.value;
      });
      return Object.keys(groups)
        .sort()
        .map((k) => groups[k]);
    }

    updateAvailability(options) {
      if (!this.picker) return;
      this.picker.querySelectorAll('input[data-option-index]').forEach((input) => {
        const index = Number(input.dataset.optionIndex);
        const probe = options.slice();
        probe[index] = input.value;
        const match = this.variants.find((v) => v.options.every((o, i) => probe[i] === o));
        input.closest('.variant').classList.toggle('is-unavailable', !match || !match.available);
      });
      this.picker.querySelectorAll('fieldset').forEach((fs) => {
        const checked = fs.querySelector('input:checked');
        const label = fs.querySelector('[data-selected-label]');
        if (checked && label) label.textContent = checked.value;
      });
    }

    async onVariantChange() {
      const options = this.selectedOptions();
      this.updateAvailability(options);
      const variant = this.variants.find((v) => v.options.every((o, i) => options[i] === o));
      const addButtons = this.querySelectorAll('[data-add-button], [id^="StickyAdd-"]');
      if (!variant) {
        addButtons.forEach((b) => {
          b.disabled = true;
          b.textContent = theme.strings.unavailable;
        });
        return;
      }
      this.querySelectorAll('[data-variant-id]').forEach((input) => (input.value = variant.id));
      if (this.dataset.updateUrl === 'true') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
      if (variant.featured_media) this.querySelector('media-gallery')?.select(variant.featured_media.id);

      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      try {
        const res = await fetch(`${this.dataset.url}?variant=${variant.id}&section_id=${this.sectionId}`, {
          signal: this.controller.signal
        });
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        this.querySelectorAll('[data-variant-region]').forEach((region) => {
          const fresh = doc.getElementById(region.id);
          if (!fresh) return;
          if (region.tagName === 'BUTTON') {
            region.disabled = fresh.disabled;
            region.textContent = fresh.textContent;
          } else if (region.id.startsWith('BuyButtons-')) {
            const freshBtn = fresh.querySelector('[data-add-button]');
            const btn = region.querySelector('[data-add-button]');
            if (freshBtn && btn) {
              btn.disabled = freshBtn.disabled;
              btn.textContent = freshBtn.textContent;
            }
            const freshQty = fresh.querySelector('[data-quantity-input]');
            const qty = region.querySelector('[data-quantity-input]');
            if (freshQty && qty) {
              ['min', 'max', 'step'].forEach((attr) => {
                if (freshQty.hasAttribute(attr)) qty.setAttribute(attr, freshQty.getAttribute(attr));
                else qty.removeAttribute(attr);
              });
              const min = Number(qty.min) || 1;
              if (Number(qty.value) < min) qty.value = min;
            }
          } else {
            region.innerHTML = fresh.innerHTML;
            region.className = fresh.className;
          }
        });
        this.oneTimePrice = null;
        this.applyPlanMode();
      } catch (e) {
        if (e.name !== 'AbortError') console.error(e);
      }
    }

    initBuyingMode() {
      this.mode = this.querySelector('[data-buying-mode]');
      if (!this.mode) return;
      this.mode.addEventListener('click', (e) => {
        const btn = e.target.closest('button[data-plan-mode]');
        if (!btn) return;
        this.mode.querySelectorAll('button[data-plan-mode]').forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
        this.applyPlanMode();
      });
      this.mode.addEventListener('change', () => this.applyPlanMode());
      this.applyPlanMode();
    }

    applyPlanMode() {
      if (!this.mode) return;
      const active = this.mode.querySelector('button[aria-pressed="true"]');
      const planMode = active && active.dataset.planMode === 'plan';
      const plans = this.querySelector('[id^="Plans-"]');
      if (plans) {
        plans.hidden = !planMode;
        plans.querySelectorAll('input[name="selling_plan"]').forEach((input) => (input.disabled = !planMode));
      }
      const priceEl = this.querySelector('[data-one-time-price] .price b');
      if (!priceEl) return;
      if (!this.oneTimePrice || !planMode) this.oneTimePrice = priceEl.innerHTML;
      const checked = plans && plans.querySelector('input[name="selling_plan"]:checked');
      priceEl.innerHTML = planMode && checked ? checked.dataset.planPrice : this.oneTimePrice;
    }

    initSticky() {
      const bar = this.querySelector('[data-sticky-atc]');
      const add = this.querySelector('[data-add-button]');
      if (!bar || !add || !('IntersectionObserver' in window)) return;
      const btn = bar.querySelector('button');
      new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        bar.classList.toggle('show', show);
        bar.setAttribute('aria-hidden', String(!show));
        if (btn) btn.tabIndex = show ? 0 : -1;
      }).observe(add);
    }
  }
  if (!customElements.get('product-info')) customElements.define('product-info', ProductInfo);

  class GiftCardRecipient extends HTMLElement {
    connectedCallback() {
      this.toggle = this.querySelector('[data-recipient-toggle]');
      this.fields = this.querySelector('[data-recipient-fields]');
      this.offset = this.querySelector('[data-recipient-offset]');
      if (!this.toggle || !this.fields) return;
      this.toggle.addEventListener('change', () => this.sync());
      this.sync();
    }

    sync() {
      const on = this.toggle.checked;
      this.fields.hidden = !on;
      this.fields.querySelectorAll('input, textarea').forEach((el) => (el.disabled = !on));
      if (this.offset) this.offset.value = String(new Date().getTimezoneOffset());
      const email = this.fields.querySelector('input[type="email"]');
      if (email) email.required = on;
    }
  }
  if (!customElements.get('gift-card-recipient')) customElements.define('gift-card-recipient', GiftCardRecipient);
})();
